import { Children, ReactNode, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { claudeTokens as t } from '@/pages/HomeV2/theme';

const Frame = styled.div`position: relative; min-width: 0;`;
const Track = styled.div`
    display: flex; align-items: stretch; overflow-x: auto; scroll-snap-type: x mandatory;
    scrollbar-width: none; &::-webkit-scrollbar { display: none; }
`;
const Page = styled.div<{ columns: number }>`
    flex: 0 0 100%; min-width: 0; display: grid;
    grid-template-columns: repeat(${p => p.columns}, minmax(0, 1fr)); gap: 16px;
    scroll-snap-align: start; align-items: stretch;
    > * { min-width: 0; }
`;
const Button = styled.button<{ side: 'left' | 'right' }>`
    position: absolute; top: 50%; transform: translateY(-50%);
    ${p => p.side}: -20px; z-index: 2; width: 40px; height: 40px;
    border: 1px solid ${t.color.border}; border-radius: 50%; background: ${t.color.surface};
    color: ${t.color.text1}; padding: 0; display: inline-flex; align-items: center;
    justify-content: center; cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,.12);
    svg { display: block; width: 24px; height: 24px; }
    &:disabled { opacity: .35; cursor: default; }
    &:focus-visible { outline: 2px solid ${t.color.accent}; outline-offset: 2px; }
`;
export const HomeCarouselCount = styled.span`
    align-self: center; font-size: 13px; color: ${t.color.text2}; white-space: nowrap;
`;

/** Shared home carousel: fixed page geometry, responsive columns, no wrapping at ends. */
export default function HomeCarousel({ children, label, resetKey, onPageChange }: {
    children: ReactNode; label: string; resetKey?: string;
    onPageChange?: (page: number, total: number) => void;
}) {
    const [columns, setColumns] = useState(() => window.innerWidth < 1024 ? 2 : 4);
    const [page, setPage] = useState(0);
    const target = useRef(0);
    const track = useRef<HTMLDivElement>(null);
    const timer = useRef<number>();
    const items = Children.toArray(children);
    const total = Math.ceil(items.length / columns);
    const callback = useRef(onPageChange);
    callback.current = onPageChange;
    useEffect(() => {
        const query = window.matchMedia('(min-width: 1024px)');
        const change = () => setColumns(query.matches ? 4 : 2);
        change(); query.addEventListener('change', change);
        return () => query.removeEventListener('change', change);
    }, []);
    useEffect(() => {
        window.clearTimeout(timer.current);
        target.current = 0; setPage(0);
        track.current?.scrollTo({ left: 0, behavior: 'instant' });
        callback.current?.(1, total);
        // A resized viewport must retain exact snap alignment even when column count stays the same.
        const observer = new ResizeObserver(() => {
            if (track.current) track.current.scrollTo({ left: target.current * track.current.clientWidth, behavior: 'instant' });
        });
        if (track.current) observer.observe(track.current);
        return () => { observer.disconnect(); window.clearTimeout(timer.current); };
    }, [columns, total, resetKey]);
    useEffect(() => { callback.current?.(page + 1, total); }, [page, total]);
    const move = (delta: number) => {
        target.current = Math.max(0, Math.min(total - 1, target.current + delta));
        setPage(target.current);
        track.current?.scrollTo({ left: target.current * track.current.clientWidth,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    return <Frame data-home-carousel={label}>
        <Track ref={track} aria-label={label} data-home-carousel-track onScroll={() => {
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => {
                const el = track.current;
                if (!el?.clientWidth) return;
                target.current = Math.max(0, Math.min(total - 1, Math.round(el.scrollLeft / el.clientWidth)));
                setPage(target.current);
            }, 150);
        }}>
            {Array.from({ length: total }, (_, index) => <Page key={index} columns={columns} data-home-carousel-page={index + 1}>
                {items.slice(index * columns, (index + 1) * columns)}
            </Page>)}
        </Track>
        {total > 1 && <>
            <Button type="button" side="left" aria-label={`이전 ${label}`} disabled={page === 0} onClick={() => move(-1)}><ChevronLeft /></Button>
            <Button type="button" side="right" aria-label={`다음 ${label}`} disabled={page >= total - 1} onClick={() => move(1)}><ChevronRight /></Button>
        </>}
    </Frame>;
}
